import type { ReactNode } from "react";
import {
  FwfLogo,
  Icon,
  Kicker,
  Panel,
  ProductMark,
  Stack,
  Subtitle,
  Title,
} from "@fwf/fwfa-design-system/react";

function BrowserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5m-15-3h13.5a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V5.25a1.5 1.5 0 0 1 1.5-1.5Z" />
    </svg>
  );
}

function SetupIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m13.5 2.25-9.75 12h8.25l-1.5 7.5 9.75-12H12l1.5-7.5Z" />
    </svg>
  );
}

function SwitchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m7.5 21-4.5-4.5m0 0L7.5 12M3 16.5h13.5M16.5 3 21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  );
}

function WorkspaceIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.49 0 4.5-4.03 4.5-9S14.49 3 12 3 7.5 7.03 7.5 12s2.01 9 4.5 9ZM3.28 9h17.44M3.28 15h17.44" />
    </svg>
  );
}

interface FeatureProps {
  icon: ReactNode;
  title: string;
  description: string;
}

function Feature({ icon, title, description }: FeatureProps) {
  return (
    <li className="landing-capability">
      <Icon className="landing-capability-icon">{icon}</Icon>
      <Stack className="landing-capability-copy">
        <Title as="h3" className="fwfa-title--component">
          {title}
        </Title>
        <Subtitle>{description}</Subtitle>
      </Stack>
    </li>
  );
}

const setupSteps = [
  {
    title: "Connect your project",
    description: "Choose the codebase you want to work on.",
  },
  {
    title: "Pick an agent",
    description: "Start with Codex, Claude, Copilot or Gemini.",
  },
  {
    title: "Open the workspace",
    description: "FreshVibe prepares the cloud environment and shows each step.",
  },
];

export default function Landing() {
  return (
    <div className="fwfa-root landing-page" data-fwf-app-theme="dark" data-testid="freshvibe-homepage">
      <header className="landing-header">
        <nav className="landing-nav" aria-label="Primary navigation">
          <a className="landing-brand-link" href="/" aria-label="FreshVibe home">
            <ProductMark variant="wordmark" size="sm" />
          </a>
          <a className="fwfa-button fwfa-button--secondary" href="/api/auth/login">
            Sign in
          </a>
        </nav>
      </header>

      <main>
        <section className="landing-hero" aria-labelledby="landing-title">
          <div className="landing-hero-inner">
            <Stack className="landing-hero-stack">
              <Kicker className="landing-kicker">A cloud workspace for AI coding</Kicker>
              <Title as="h1" id="landing-title" className="landing-title">
                Start building with the AI you prefer.
                <span className="landing-title-mark"> We’ll handle the setup.</span>
              </Title>
              <Subtitle className="landing-hero-copy">
                Use Codex, Claude, Copilot or Gemini in an isolated workspace. FreshVibe handles the setup and keeps every step visible.
              </Subtitle>
              <div className="landing-actions">
                <a
                  className="fwfa-button fwfa-button--primary landing-primary-action"
                  href="/api/auth/login"
                  data-testid="landing-primary-action"
                >
                  <BrowserIcon />
                  Open FreshVibe
                </a>
              </div>
              <p className="landing-action-note">Runs in your browser. No local setup required.</p>
            </Stack>

            <aside className="landing-flow" aria-labelledby="landing-flow-title">
              <Panel className="landing-flow-panel">
                <Kicker>What happens next</Kicker>
                <Title as="h2" id="landing-flow-title" className="fwfa-title--component">
                  From codebase to coding
                </Title>
                <ol className="landing-flow-steps">
                  {setupSteps.map((step, index) => (
                    <li className="landing-flow-step" key={step.title}>
                      <span className="landing-flow-number" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <strong>{step.title}</strong>
                        <p>{step.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </Panel>
            </aside>
          </div>
        </section>

        <section className="landing-section" aria-labelledby="landing-features-title">
          <Stack className="landing-section-heading">
            <Title as="h2" id="landing-features-title" className="fwfa-title--section">
              Change agents, not environments
            </Title>
            <Subtitle className="landing-section-copy">
              Your project stays in its own workspace, so you can choose the agent that fits the work without rebuilding your setup.
            </Subtitle>
          </Stack>

          <ul className="landing-capabilities">
            <Feature
              icon={<SetupIcon />}
              title="Guided setup"
              description="Follow a short setup that shows what FreshVibe is doing and what it needs from you."
            />
            <Feature
              icon={<WorkspaceIcon />}
              title="Workspace isolation"
              description="Each project runs in a dedicated cloud workspace, away from your local machine."
            />
            <Feature
              icon={<SwitchIcon />}
              title="Agent choice"
              description="Use Codex, Claude, Copilot or Gemini with the same codebase and project context."
            />
          </ul>
        </section>

        <section className="landing-section landing-start" aria-labelledby="landing-start-title">
          <Stack className="landing-start-copy">
            <Kicker>Ready when you are</Kicker>
            <Title as="h2" id="landing-start-title" className="fwfa-title--section">
              Start with your codebase
            </Title>
            <Subtitle>Sign in, choose a project and let FreshVibe prepare the workspace.</Subtitle>
          </Stack>
          <a className="fwfa-button fwfa-button--primary" href="/api/auth/login">
            Start in browser
          </a>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <FwfLogo size="sm" />
            <span>Built by Freshwater Futures</span>
          </div>
          <div className="landing-footer-meta">
            <span>
              {__BUILD_INFO__.version}+{__BUILD_INFO__.buildNumber} ({__BUILD_INFO__.branch}@{__BUILD_INFO__.commit})
            </span>
            <a href="https://freshwaterfutures.com/freshvibe/privacy">Privacy</a>
            <a href="https://freshwaterfutures.com/freshvibe/terms">Terms</a>
            <a href="https://freshwaterfutures.com">freshwaterfutures.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
